import axios from 'axios';
import { useEffect, useState, useCallback } from 'react';
import { auth } from '../firebase';
import { API_BASE } from './runtimeEnv';

const API_URL = API_BASE;

// Single axios instance for all end-user calls. Every request carries the current
// user's fresh Firebase ID token as a Bearer — exactly what the mobile app sends and
// what backend `verifyToken` (middleware/firebaseAuth.js) expects.
export const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    // getIdToken() returns a cached token and silently refreshes when near expiry.
    const token = await user.getIdToken();
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // A 401 means the token was rejected/expired — drop the session so the guard
    // bounces the user back to /login rather than showing a broken shell.
    if (err?.response?.status === 401) {
      auth.signOut().catch(() => {});
    }
    return Promise.reject(err);
  }
);

// Shared response cache (path -> { version?, data }) behind both hooks below and the
// dashboard prefetch. Lets a page paint instantly from data fetched earlier (on login or
// a prior visit) while it revalidates in the background.
const responseCache = new Map();

// Small fetch hook used across the views, now stale-while-revalidate: if the path is
// already in the cache (warmed by prefetchDashboard on login, or a prior visit), it paints
// immediately and refreshes silently in the background — so navigating between pages feels
// instant instead of flashing a spinner each time.
// `reload()` forces a loading refresh; `silentReload()` refreshes in place (used after
// in-page mutations like send-message / add-photo).
export function useApi(path, { skip = false } = {}) {
  const cached = path ? responseCache.get(path) : null;
  const [data, setData] = useState(cached ? cached.data : null);
  const [loading, setLoading] = useState(!skip && !cached);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (silent) => {
    if (skip || !path) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await api.get(path);
      responseCache.set(path, { data: res.data });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'request_failed');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [path, skip]);

  const reload = useCallback(() => fetchData(false), [fetchData]);
  const silentReload = useCallback(() => fetchData(true), [fetchData]);

  // On mount / path change: revalidate silently when we already have cached data (no
  // spinner), otherwise do a normal loading fetch.
  useEffect(() => {
    fetchData(!!(path && responseCache.get(path)));
  }, [fetchData, path]);

  return { data, loading, error, reload, silentReload, setData };
}

// Stale-while-revalidate: any cached response renders IMMEDIATELY (loading stays false),
// even before the manifest's version arrives. Once the version is known, a mismatch
// triggers a silent background refetch that swaps the data in place. Pages like Profile
// and Badges feel instant on revisit instead of re-downloading unchanged payloads.
export function useCachedApi(path, version, { skip = false } = {}) {
  const cached = responseCache.get(path);
  const [data, setData] = useState(cached ? cached.data : null);
  const [loading, setLoading] = useState(!skip && !cached);
  const [error, setError] = useState(null);

  useEffect(() => {
    // version === undefined means the manifest hasn't arrived yet — show whatever is
    // cached and wait rather than firing a fetch we might immediately repeat.
    if (skip || !path || version === undefined) return;
    const hit = responseCache.get(path);
    if (hit && hit.version === version) {
      setData(hit.data);
      setLoading(false);
      return;
    }
    let cancelled = false;
    if (!hit) setLoading(true); // stale data keeps rendering during a silent refresh
    setError(null);
    api.get(path)
      .then((res) => {
        responseCache.set(path, { version, data: res.data });
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled && !hit) setError(err?.response?.data?.error || err.message || 'request_failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path, version, skip]);

  return { data, loading, error };
}

// Near-real-time spine for web. The manifest carries the user's `activeMoments` ("Happening
// now") and per-resource version stamps; refreshing it drives both LiveMoments (reads
// activeMoments directly) and every useCachedApi page (refetches on a stamp change). Previously
// this fetched once per mount with no interval and no push, so a friend's new message / check-in /
// new live moment never appeared on web without a manual reload (QA P0-2).
//
// One shared store, ref-counted across all useManifest() consumers: a single interval polls every
// ~20s and window focus / tab-visibility force an immediate revalidate, so returning to the tab is
// instant. A short TTL still dedupes the burst of mounts on a page load / focus.
const MANIFEST_TTL_MS = 15_000;
const MANIFEST_REFRESH_MS = 20_000;
// COST-3: a tab the user isn't looking at doesn't need 3 polls a minute — each poll is a
// full manifest query pass server-side. Hidden tabs drop to 60s; returning to the tab
// force-revalidates immediately (the visibility handler below), so nothing feels stale.
const MANIFEST_REFRESH_HIDDEN_MS = 60_000;
let manifestState = null;       // last good manifest data (shared)
let manifestFetchedAt = 0;
let manifestInFlight = null;    // promise dedupe for concurrent callers
const manifestSubs = new Set(); // setData callbacks of mounted consumers
let manifestTimer = null;

function fetchManifest(force = false) {
  if (manifestInFlight) return manifestInFlight;
  if (!force && manifestState && Date.now() - manifestFetchedAt < MANIFEST_TTL_MS) {
    return Promise.resolve(manifestState);
  }
  manifestInFlight = api.get('/sync/manifest')
    .then((r) => {
      manifestState = r.data;
      manifestFetchedAt = Date.now();
      manifestSubs.forEach((fn) => fn(manifestState));
      return manifestState;
    })
    .catch(() => manifestState) // keep showing the last good manifest on a transient failure
    .finally(() => { manifestInFlight = null; });
  return manifestInFlight;
}

// Force a refresh from outside the hook lifecycle — used by the FCM push handler so an incoming
// nudge updates the UI immediately rather than waiting for the next poll tick.
export function refreshManifest() { return fetchManifest(true); }

// Flush every cached response + the shared manifest. Called on an account change (sign-in/out, or
// switching to/from a managed child profile) so we never show the previous account's data.
export function resetApiCaches() {
  responseCache.clear();
  manifestState = null;
  manifestFetchedAt = 0;
}

const onManifestFocus = () => fetchManifest(true);
const onManifestVisibility = () => { if (document.visibilityState === 'visible') fetchManifest(true); };

// Self-rescheduling tick (instead of a fixed setInterval) so the cadence re-reads the tab's
// visibility every cycle: 20s in the foreground, 60s hidden (COST-3). The generation counter
// makes stop() definitive — an in-flight tick from a previous generation never reschedules,
// so a stop→start during a fetch can't fork two polling chains.
let manifestPollGen = 0;
function scheduleManifestTick() {
  const gen = manifestPollGen;
  const delay = document.visibilityState === 'hidden' ? MANIFEST_REFRESH_HIDDEN_MS : MANIFEST_REFRESH_MS;
  manifestTimer = setTimeout(() => {
    fetchManifest(true).finally(() => {
      if (manifestTimer !== null && gen === manifestPollGen) scheduleManifestTick();
    });
  }, delay);
}

function startManifestPolling() {
  if (manifestTimer !== null) return;
  scheduleManifestTick();
  window.addEventListener('focus', onManifestFocus);
  document.addEventListener('visibilitychange', onManifestVisibility);
}

function stopManifestPolling() {
  if (manifestSubs.size > 0 || manifestTimer === null) return;
  clearTimeout(manifestTimer);
  manifestTimer = null;
  manifestPollGen += 1;
  window.removeEventListener('focus', onManifestFocus);
  document.removeEventListener('visibilitychange', onManifestVisibility);
}

export function useManifest() {
  const [data, setData] = useState(manifestState);
  const [loading, setLoading] = useState(!manifestState);
  useEffect(() => {
    let cancelled = false;
    const onData = (m) => { if (!cancelled) { setData(m); setLoading(false); } };
    manifestSubs.add(onData);
    startManifestPolling();
    // Paint immediately from shared state if present, then revalidate (TTL-deduped).
    if (manifestState) onData(manifestState);
    fetchManifest(false).then((m) => { if (m) onData(m); else if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      manifestSubs.delete(onData);
      stopManifestPolling();
    };
  }, []);
  return { manifest: data, loading };
}

// Version stamp for a manifest resource: undefined while the manifest loads (callers
// wait), null when the manifest has no stamp (treated as a concrete version).
export const resourceVersion = (manifest, loading, name) =>
  manifest?.resources?.[name] ?? (loading ? undefined : null);

// Warm the cache for the data behind every main page as soon as the shell mounts, so
// navigation paints instantly instead of fetching on arrival. Every path is version-keyed
// against the manifest's resource stamps and only refetched when its stamp moved (PERF-11).
//
// Keyed on the STAMP MAP, not generatedAt: the manifest payload also carries live-moment
// state (e.g. a rave's ticking countdown) that changes generatedAt every poll — the old
// generatedAt key made an active rave re-run the whole 11-request prefetch every 20s per tab
// even though no page data had changed.
let lastPrefetchKey = null;

export function prefetchDashboard(manifest) {
  if (!manifest) return;
  const r = manifest.resources || {};
  const key = JSON.stringify(r);
  if (key === lastPrefetchKey) return;
  lastPrefetchKey = key;
  const versioned = {
    '/auth/profile': r.profile ?? null,
    '/recaps/me': r.moments ?? null,
    // week/month feed Insights; range=all feeds the Moments page (full history, PAR-4).
    '/recaps/moments?range=week': r.moments ?? null,
    '/recaps/moments?range=month': r.moments ?? null,
    '/recaps/moments?range=all': r.moments ?? null,
    '/badges/me': `${r.badges}|${r.moments}|${r.friends}|${r.circles}`,
    '/tags': r.tags ?? null,
    '/home/sections': r.homeSections ?? null,
    // These pages read via useApi (which ignores `version` and revalidates on visit), but
    // stamping them here stops the prefetch from re-fetching them on every unrelated change —
    // they used to be fetched unconditionally each run.
    '/crews': r.circles ?? null,
    '/friends/list': `${r.friends}|${r.moments}`,
    '/friends/companions': `${r.friends}|${r.circles}|${r.moments}`,
    '/quests/suggested': r.quests ?? null,
  };

  Object.entries(versioned).forEach(([path, version]) => {
    if (responseCache.get(path)?.version === version) return; // already fresh
    api.get(path).then((res) => responseCache.set(path, { version, data: res.data })).catch(() => {});
  });
}
